import { useMutation, useQuery } from '@apollo/client'
import { ALL_TIME_OVERRIDES } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { OldAddButton, Header } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import { format, formatISO, parseISO } from 'date-fns'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  CREATE_TIME_OVERRIDE,
  DELETE_TIME_OVERRIDE,
  PATCH_TIME_OVERRIDE,
} from './mutations'
import type { TimeOverrideNode } from './types.graphql'
import type { OverrideOrAddonForm } from './types'
import { EditAddonPrompt } from './EditAddonPrompt'
import { PERMISSIONS } from '@/utils/permissions'

const Wrapper = styled(Card)`
  position: static;
  margin: 1rem 0;
`

const H2 = styled.h2`
  display: flex;

  margin: 1rem 0 0 8px;
  font-weight: 600;

  div {
    margin-left: 1rem;
  }
`

interface TimeOverridesProps {
  datetimeStart: Date
  datetimeEnd: Date
  userId: string
  onChange?: () => void
}

export const TimeOverrides: React.JSX.CFC<TimeOverridesProps> = ({
  datetimeStart,
  datetimeEnd,
  userId,
  onChange,
}) => {
  const translations = useTranslate({
    timeOverrides: 'salary.salary-code-overrides',

    start: 'common.start',
    end: 'common.end',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',

    deleteTimeOverride: 'salary.delete-time-override',
    deleteTimeOverrideConfirm: 'salary.delete-time-override-confirm',

    toast: {
      successCreate: 'salary.time-override-create-success',
      successUpdate: 'salary.time-override-update-success',
      successDelete: 'salary.time-override-delete-success',
    },
    comment: 'common.comment',
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeoverride, true)
  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeoverride, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeoverride, true)

  const { data, loading } = useQuery(ALL_TIME_OVERRIDES, {
    variables: {
      datetimeGte: formatISO(datetimeStart),
      datetimeLt: formatISO(datetimeEnd),
      user: userId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_TIME_OVERRIDES,
      variables: {
        datetimeGte: formatISO(datetimeStart),
        datetimeLt: formatISO(datetimeEnd),
        user: userId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createTimeOverride] = useMutation(CREATE_TIME_OVERRIDE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
      onChange?.()
    },
  })

  const [patchTimeOverride] = useMutation(PATCH_TIME_OVERRIDE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
      onChange?.()
    },
  })

  const [deleteTimeOverride] = useMutation(DELETE_TIME_OVERRIDE, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
      onChange?.()
    },
  })

  const timeOverrides = useNodes(data?.allTimeOverrides)

  const handleAdd = async () => {
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={{
          datetimeStart: datetimeStart,
          datetimeEnd: datetimeStart,
          salaryCode: null,
          overrideOtherAddons: false,
          comment: '',
        }}
        userId={userId}
        isOverride={true}
        onSubmit={resolve}
      />
    ))

    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return

    void createTimeOverride({
      variables: {
        input: {
          user: userId,
          datetimeStart: formatISO(data.datetimeStart),
          datetimeEnd: formatISO(data.datetimeEnd),
          salaryCode: data.salaryCode,
          comment: data.comment,
        },
      },
    })
  }

  const handleEdit = async (override: TimeOverrideNode) => {
    const initialData = {
      datetimeStart: parseISO(override.datetimeStart),
      datetimeEnd: parseISO(override.datetimeEnd),
      salaryCode: override.salaryCode?.id ?? '',
      overrideOtherAddons: false,
      comment: override.comment,
    }
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        isOverride={true}
        initialData={initialData}
        onSubmit={resolve}
        userId={userId}
      />
    ))

    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return

    void patchTimeOverride({
      variables: {
        id: override.id,
        input: {
          datetimeStart: formatISO(data.datetimeStart),
          datetimeEnd: formatISO(data.datetimeEnd),
          salaryCode: data.salaryCode,
          comment: data.comment,
        },
      },
    })
  }

  const handleDelete = async (override: TimeOverrideNode) => {
    const { data } = await confirm(
      translations.deleteTimeOverrideConfirm,
      translations.deleteTimeOverride
    )

    if (!data) return

    void deleteTimeOverride({
      variables: {
        id: override.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <Header>
        <H2>{translations.timeOverrides}</H2>
        {loading && <Loader.Spinner />}
        {!loading && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </Header>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.start}</th>
              <th>{translations.end}</th>
              <th>{translations.salaryCode}</th>
              <th>{translations.comment}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {timeOverrides.map(timeOverride => (
              <tr key={timeOverride.id}>
                <td>
                  {format(
                    parseISO(timeOverride.datetimeStart),
                    'dd. MMM HH:mm'
                  )}
                </td>
                <td>
                  {format(parseISO(timeOverride.datetimeEnd), 'dd. MMM HH:mm')}
                </td>
                <td>
                  {timeOverride.salaryCode ? timeOverride.salaryCode.name : '-'}
                </td>
                <td>{timeOverride.comment ? timeOverride.comment : '-'}</td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(timeOverride)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(timeOverride)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
